/** Who a criterion can describe. A clergy field says nothing about a lay member. */
export type Audience = "clergy" | "laity" | "everyone";

export type TargetType = "ALL" | "CRITERIA";

/** Keys whose options come from the meta bundle, keyed the same way it is. */
export const META_FIELDS = [
  { key: "userType", label: "User type", audience: "everyone" },
  { key: "cradleOrConvert", label: "Cradle or converted status", audience: "everyone" },
  { key: "jurisdiction", label: "Jurisdiction", audience: "everyone" },
  { key: "diocese", label: "Diocese", audience: "everyone" },
  { key: "parishRole", label: "Parish role", audience: "laity" },
  { key: "academicDegree", label: "Academic degree", audience: "laity" },
  { key: "clericalRank", label: "Clerical rank", audience: "clergy" },
  { key: "clericalStatus", label: "Clerical status", audience: "clergy" },
  { key: "parishType", label: "Parish type", audience: "clergy" },
  { key: "seminarySchool", label: "Seminary school", audience: "clergy" },
] as const satisfies readonly { key: string; label: string; audience: Audience }[];

export type MetaFieldKey = (typeof META_FIELDS)[number]["key"];

/** The backend has no option source for `school`, so it is not offered. */
export interface TargetCriteria {
  userType: number[];
  ageGTE: string;
  ageLTE: string;
  gender: number[];
  cradleOrConvert: number[];
  jurisdiction: number[];
  diocese: number[];
  state: number[];
  city: number[];
  school: number[];
  parishRole: number[];
  academicDegree: number[];
  clericalRank: number[];
  clericalStatus: number[];
  parishType: number[];
  seminarySchool: number[];
}

export interface TargetOptions {
  startDate: string | null;
  endDate: string | null;
  targetType: TargetType | null;
  criteria: TargetCriteria;
}

export interface MetaOption {
  id: number;
  label: string;
  keyName?: string;
}

export type MetaBundle = Partial<Record<MetaFieldKey, MetaOption[]>>;

export interface StateOption {
  id: number;
  state: string;
}

export interface CityOption {
  id: number;
  city: string;
  stateId: number | null;
}

export const GENDER_OPTIONS: MetaOption[] = [
  { id: 1, label: "Male" },
  { id: 2, label: "Female" },
];

export function emptyCriteria(): TargetCriteria {
  return {
    userType: [],
    ageGTE: "",
    ageLTE: "",
    gender: [],
    cradleOrConvert: [],
    jurisdiction: [],
    diocese: [],
    state: [],
    city: [],
    school: [],
    parishRole: [],
    academicDegree: [],
    clericalRank: [],
    clericalStatus: [],
    parishType: [],
    seminarySchool: [],
  };
}

/** Fills the gaps a partial response leaves, so every control stays controlled. */
export function withDefaults(criteria: Partial<TargetCriteria> | null | undefined): TargetCriteria {
  return { ...emptyCriteria(), ...(criteria ?? {}) };
}

/**
 * Which audiences the chosen user types cover. Choosing nothing narrows to no
 * audience at all, which is what hides the audience-specific fields.
 */
export function selectedAudiences(
  userTypeIds: number[],
  userTypeOptions: MetaOption[],
): Set<Audience> {
  const audiences = new Set<Audience>();
  for (const id of userTypeIds) {
    const key = (userTypeOptions.find((o) => o.id === id)?.keyName ?? "").toLowerCase();
    if (key.includes("clergy")) audiences.add("clergy");
    else if (key.includes("laity") || key.includes("laic")) audiences.add("laity");
    else {
      audiences.add("clergy");
      audiences.add("laity");
    }
  }
  return audiences;
}

export function isFieldVisible(audience: Audience, selected: Set<Audience>): boolean {
  return audience === "everyone" || selected.has(audience);
}

/** The server rejects a CRITERIA target that narrows nothing. */
export function hasAnyCriterion(criteria: TargetCriteria): boolean {
  return Object.entries(criteria).some(([, value]) =>
    Array.isArray(value) ? value.length > 0 : String(value ?? "").trim() !== "",
  );
}

export function citiesForStates(cities: CityOption[], stateIds: number[]): CityOption[] {
  if (stateIds.length === 0) return [];
  const wanted = new Set(stateIds);
  return cities.filter((c) => c.stateId !== null && wanted.has(c.stateId));
}
